import { useEffect, useRef, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { Loader2, UploadCloud, FileText, X } from 'lucide-react';
import { logGlQuote, attachQuoteSourcePdf, listCarriers } from '@/services/quotes.service.js';
import TermPicker, { termMonthsOf } from '@/components/TermPicker.jsx';
import { presentSaveNotices } from '@/lib/quoteCompleteness.js';

// Live $#,### formatting for currency inputs — so $1,000,000 vs $100,000 is
// obvious while typing. Stored verbatim; num() strips $/, before submit.
function formatMoney(v) {
  if (v == null) return '';
  const s = String(v).replace(/[^0-9.]/g, '');
  if (s === '') return '';
  const [intRaw, ...rest] = s.split('.');
  const intp = (intRaw.replace(/^0+(?=\d)/, '') || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return '$' + intp + (rest.length ? '.' + rest.join('').slice(0, 2) : '');
}

// COVERAGE-LINE-ENVELOPE-SPEC §7 Slice 4c — broker "Log GL quote" form, Airport-GL
// structure. Native TRACON manual entry: the broker types the carrier's Airport
// GL quote (Premises + Medical + Products + Fire Legal + Hangarkeepers + Personal
// & Advertising + TRIA + War) and it presents in the client's Tower as a clean GL
// card and is bindable. Builds typed coverage lines and POSTs via logGlQuote
// (policy_type:'commercial_gl'). Premises is the base; the rest are toggled on.
//
// Personal & Advertising Injury has TWO carrier shapes (2026-09-25, Knight
// Flight): Beacon quotes it as ONE coverage with one premium (line_type
// personal_advertising); AIM/HDI quotes Coverage C and D with a premium each
// (personal_injury + advertising_injury). The broker says which shape the
// carrier used; the sheet never guesses it from the numbers.
//
// Edit-in-place: pass `quote` (GET /quotes/:id/full, with .lines) and the sheet
// opens prefilled (seedFromQuote, the inverse of buildLines) and saves through
// log-hangar's quote_id update path (updateCanonicalQuote replaces the lines).

function num(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(String(v).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
}

// Limits bag from [key, value] pairs, dropping blanks (a cleared field is an
// absent limit, never a 0).
function limFrom(...pairs) {
  const o = {};
  for (const [k, v] of pairs) { const n = num(v); if (n != null) o[k] = n; }
  return o;
}

// §4d — optional/selected for an electable coverage. optional ⇒ client can
// decline in Tower; selected = its default checkbox state ("as expiring").
function electFrom(f, optKey, expKey) {
  return { optional: !!f[optKey], selected: f[optKey] ? !!f[expKey] : true };
}

// The coverage lines a form state writes. Pure function of `f` (exported for
// the round-trip guard: buildLinesFrom(seedFromQuote(q)) must give back q's lines).
export function buildLinesFrom(f) {
  const lim = limFrom;
  const elect = (optKey, expKey) => electFrom(f, optKey, expKey);
  const lines = [];
  lines.push({ line_type: 'premises', subject_kind: null, optional: false, premium: num(f.premises_premium),
    limits: lim(['each_occurrence', f.premises_each_occurrence], ['each_person', f.premises_each_person]), payload: {} });
  if (f.medical_on && num(f.medical_premium) != null) {
    lines.push({ line_type: 'medical_expense', subject_kind: null, ...elect('medical_optional', 'medical_expiring'), premium: num(f.medical_premium),
      limits: lim(['each_occurrence', f.medical_each_occurrence], ['each_person', f.medical_each_person], ['aggregate', f.medical_aggregate]), payload: {} });
  }
  if (f.products_on && num(f.products_premium) != null) {
    const payload = {}; const sl = num(f.products_sub_limit); if (sl != null) payload.sub_limit = sl;
    lines.push({ line_type: 'products', subject_kind: null, ...elect('products_optional', 'products_expiring'), premium: num(f.products_premium),
      limits: lim(['each_occurrence', f.products_each_occurrence], ['aggregate', f.products_aggregate]), payload });
  }
  if (f.fire_on && num(f.fire_premium) != null) {
    lines.push({ line_type: 'fire_legal', subject_kind: null, ...elect('fire_optional', 'fire_expiring'), premium: num(f.fire_premium),
      limits: lim(['each_occurrence', f.fire_each_occurrence]), payload: {} });
  }
  if (f.hk_on && num(f.hk_premium) != null) {
    lines.push({ line_type: 'hkll', subject_kind: null, ...elect('hk_optional', 'hk_expiring'), premium: num(f.hk_premium),
      limits: lim(['each_occurrence', f.hk_each_occurrence], ['each_aircraft', f.hk_each_aircraft]),
      deductibles: num(f.hk_deductible) != null ? { amount: num(f.hk_deductible) } : {}, payload: {} });
  }
  // Personal & Advertising: ONE combined line (Beacon) or the C + D pair (AIM).
  // The mode decides which types are written; the other shape is never sent.
  if (f.pa_mode === 'combined') {
    if (f.pa_on && num(f.pa_premium) != null) {
      lines.push({ line_type: 'personal_advertising', subject_kind: null, ...elect('pa_optional', 'pa_expiring'), premium: num(f.pa_premium),
        limits: lim(['each_occurrence', f.pa_each_occurrence], ['aggregate', f.pa_aggregate]), payload: {} });
    }
  } else {
    if (f.pi_on && num(f.pi_premium) != null) {
      lines.push({ line_type: 'personal_injury', subject_kind: null, ...elect('pi_optional', 'pi_expiring'), premium: num(f.pi_premium),
        limits: lim(['each_occurrence', f.pi_each_occurrence], ['aggregate', f.pi_aggregate]), payload: {} });
    }
    if (f.adv_on && num(f.adv_premium) != null) {
      lines.push({ line_type: 'advertising_injury', subject_kind: null, ...elect('adv_optional', 'adv_expiring'), premium: num(f.adv_premium),
        limits: lim(['each_occurrence', f.adv_each_occurrence], ['aggregate', f.adv_aggregate]), payload: {} });
    }
  }
  if (f.tria_on && num(f.tria_premium) != null) {
    lines.push({ line_type: 'tria', subject_kind: null, ...elect('tria_optional', 'tria_expiring'), premium: num(f.tria_premium), limits: {}, payload: {} });
  }
  if (f.war_on && num(f.war_premium) != null) {
    lines.push({ line_type: 'war', subject_kind: null, ...elect('war_optional', 'war_expiring'), premium: num(f.war_premium), limits: {}, payload: {} });
  }
  return lines;
}

export function seed() {
  return {
    carrier: '',
    effective_date: '',
    term_months: '12', // decision 14: pre-picked, sent as shown
    // Premises Operations — the base GL coverage.
    premises_each_occurrence: '500000',
    premises_each_person: '',
    premises_premium: '',
    // Medical Expenses
    medical_on: false,
    medical_optional: true,
    medical_expiring: true,
    medical_each_occurrence: '5000',
    medical_each_person: '1000',
    medical_aggregate: '5000',
    medical_premium: '',
    // Products & Completed Operations
    products_on: false,
    products_optional: false,
    products_expiring: true,
    products_each_occurrence: '1000000',
    products_aggregate: '1000000',
    products_sub_limit: '',
    products_premium: '',
    // Fire Legal Liability
    fire_on: false,
    fire_optional: true,
    fire_expiring: true,
    fire_each_occurrence: '50000',
    fire_premium: '',
    // Hangarkeeper's Legal Liability
    hk_on: false,
    hk_optional: false,
    hk_expiring: true,
    hk_each_occurrence: '',
    hk_each_aircraft: '',
    hk_deductible: '',
    hk_premium: '',
    // Personal & Advertising Injury: 'combined' (one line, Beacon) or 'separate'
    // (Coverage C + D, AIM). The carrier's document decides; the broker states it.
    pa_mode: 'separate',
    pa_on: false,
    pa_optional: false,
    pa_expiring: true,
    pa_each_occurrence: '1000000',
    pa_aggregate: '1000000',
    pa_premium: '',
    // Personal Injury
    pi_on: false,
    pi_optional: true,
    pi_expiring: true,
    pi_each_occurrence: '1000000',
    pi_aggregate: '1000000',
    pi_premium: '',
    // Advertising Injury
    adv_on: false,
    adv_optional: true,
    adv_expiring: true,
    adv_each_occurrence: '1000000',
    adv_aggregate: '1000000',
    adv_premium: '',
    // TRIA / Certified Acts of Terrorism
    tria_on: false,
    tria_optional: true,
    tria_expiring: false,
    tria_premium: '',
    // War Risk liability (Beacon offers it as an optional add-on beside TRIA)
    war_on: false,
    war_optional: true,
    war_expiring: false,
    war_premium: '',
    // Carrier's minimum policy premium (Beacon: $5,019). The pre-tax premium
    // excluding TRIA and War floors to it; those ride on top. Blank = none stated.
    minimum_premium: '',
    subject_to: '',
    source_pdf: null, // optional carrier source-quote PDF (gap A)
    make_visible: true,
  };
}

// Inverse of buildLines: seed the form from an existing envelope quote (quote
// + lines from GET /quotes/:id/full) so Edit opens prefilled. Every line type
// buildLines can write is read back here; an optional line's stored `selected`
// becomes its "on expiring policy" default. A carrier not in the dropdown list
// falls back to the free-text input (the caller flips carrierOther).
export function seedFromQuote(q) {
  const s = seed();
  if (!q) return s;
  const str = (v) => (v == null ? '' : String(v));
  s.carrier = q.carrier || '';
  s.effective_date = q.effective_date || '';
  // The stored term, or nothing: an edit never shows a 12 nobody picked.
  s.term_months = str(q.term_months);
  s.subject_to = q.subject_to || '';
  s.minimum_premium = str(q.minimum_premium);
  s.make_visible = q.client_visible === true;
  const lines = Array.isArray(q.lines) ? q.lines : [];
  const byType = (t) => lines.find((l) => l && l.line_type === t) || null;
  const lim = (l, k) => str(l?.limits?.[k]);
  // optional/expiring off a stored line: optional ⇒ its selected flag is the
  // default checkbox; required ⇒ optional=false (expiring irrelevant).
  const el = (l, pfx) => { s[`${pfx}_optional`] = l.optional === true; s[`${pfx}_expiring`] = l.optional === true ? l.selected === true : true; };
  const prem = byType('premises');
  if (prem) {
    s.premises_each_occurrence = lim(prem, 'each_occurrence');
    s.premises_each_person = lim(prem, 'each_person');
    s.premises_premium = str(prem.premium);
  }
  const med = byType('medical_expense');
  if (med) {
    s.medical_on = true; el(med, 'medical');
    s.medical_each_occurrence = lim(med, 'each_occurrence'); s.medical_each_person = lim(med, 'each_person'); s.medical_aggregate = lim(med, 'aggregate');
    s.medical_premium = str(med.premium);
  }
  const prod = byType('products');
  if (prod) {
    s.products_on = true; el(prod, 'products');
    s.products_each_occurrence = lim(prod, 'each_occurrence'); s.products_aggregate = lim(prod, 'aggregate');
    s.products_sub_limit = str(prod.payload?.sub_limit); s.products_premium = str(prod.premium);
  }
  const fire = byType('fire_legal');
  if (fire) { s.fire_on = true; el(fire, 'fire'); s.fire_each_occurrence = lim(fire, 'each_occurrence'); s.fire_premium = str(fire.premium); }
  const hk = byType('hkll');
  if (hk) {
    s.hk_on = true; el(hk, 'hk'); s.hk_each_occurrence = lim(hk, 'each_occurrence'); s.hk_each_aircraft = lim(hk, 'each_aircraft');
    s.hk_deductible = str(hk.deductibles?.amount); s.hk_premium = str(hk.premium);
  }
  const pa = byType('personal_advertising');
  const pi = byType('personal_injury');
  const adv = byType('advertising_injury');
  if (pa) {
    s.pa_mode = 'combined'; s.pa_on = true; el(pa, 'pa');
    s.pa_each_occurrence = lim(pa, 'each_occurrence'); s.pa_aggregate = lim(pa, 'aggregate'); s.pa_premium = str(pa.premium);
  } else {
    s.pa_mode = 'separate';
    if (pi) { s.pi_on = true; el(pi, 'pi'); s.pi_each_occurrence = lim(pi, 'each_occurrence'); s.pi_aggregate = lim(pi, 'aggregate'); s.pi_premium = str(pi.premium); }
    if (adv) { s.adv_on = true; el(adv, 'adv'); s.adv_each_occurrence = lim(adv, 'each_occurrence'); s.adv_aggregate = lim(adv, 'aggregate'); s.adv_premium = str(adv.premium); }
  }
  const tria = byType('tria');
  if (tria) { s.tria_on = true; el(tria, 'tria'); s.tria_premium = str(tria.premium); }
  const war = byType('war');
  if (war) { s.war_on = true; el(war, 'war'); s.war_premium = str(war.premium); }
  return s;
}

// quote: an existing envelope quote (with .lines) to EDIT in place; absent =
// log a new quote.
export default function LogGlQuoteSheet({ dealId, open, onOpenChange, onSaved, quote = null }) {
  const [f, setF] = useState(seed);
  const [saving, setSaving] = useState(false);
  const [carriers, setCarriers] = useState([]);   // dropdown options
  const [carrierOther, setCarrierOther] = useState(false); // "Other…" → free text
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const editing = !!quote?.id;
  // Reset on each open so a cancel discards edits; edit mode seeds from the quote.
  useEffect(() => { if (open) { setF(quote ? seedFromQuote(quote) : seed()); setCarrierOther(false); setDragOver(false); } }, [open, quote]);
  // Editing a quote whose carrier is not in the dropdown: show it as free text
  // rather than a blank select that would drop the name on save.
  useEffect(() => {
    if (open && editing && carriers.length > 0 && quote?.carrier && !carriers.includes(quote.carrier)) setCarrierOther(true);
  }, [open, editing, carriers, quote]);
  // Load the carrier list (brand names) for the dropdown.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    listCarriers().then((r) => { if (alive) setCarriers((r?.carriers || []).map((c) => c.vendor_name || c.label).filter(Boolean)); }).catch(() => {});
    return () => { alive = false; };
  }, [open]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  // PDF drop/select — accept a single PDF, reject anything else.
  const takeFile = (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Please choose a PDF file'); return; }
    set('source_pdf', file);
  };

  // Each toggled-on coverage requires a premium.
  const togOk = (on, premKey) => !f[on] || num(f[premKey]) != null;
  const canSave = !!f.carrier.trim() && !!f.effective_date && num(f.premises_premium) != null
    && togOk('medical_on', 'medical_premium') && togOk('products_on', 'products_premium')
    && togOk('fire_on', 'fire_premium') && togOk('hk_on', 'hk_premium')
    && (f.pa_mode === 'combined' ? togOk('pa_on', 'pa_premium') : (togOk('pi_on', 'pi_premium') && togOk('adv_on', 'adv_premium')))
    && togOk('tria_on', 'tria_premium') && togOk('war_on', 'war_premium')
    && (f.term_months === '' || termMonthsOf(f.term_months) != null) && !saving;

  // §4d — a coverage is in the DEFAULT presented total when it's selected by
  // default: not optional, OR optional-and-on-the-expiring-policy. Optional
  // coverages the client declines fall away; the bound premium follows the
  // client's checkboxes in Tower (select-quote re-derives from the election).
  const dflSel = (optKey, expKey) => !f[optKey] || f[expKey];
  // Two halves, the way utils/quote-premium.js splits them: the minimum floors
  // everything except TRIA and War, which ride on top of the floored figure.
  const floorableTotal = (num(f.premises_premium) || 0)
    + (f.medical_on && dflSel('medical_optional', 'medical_expiring') ? (num(f.medical_premium) || 0) : 0)
    + (f.products_on && dflSel('products_optional', 'products_expiring') ? (num(f.products_premium) || 0) : 0)
    + (f.fire_on && dflSel('fire_optional', 'fire_expiring') ? (num(f.fire_premium) || 0) : 0)
    + (f.hk_on && dflSel('hk_optional', 'hk_expiring') ? (num(f.hk_premium) || 0) : 0)
    + (f.pa_mode === 'combined'
      ? (f.pa_on && dflSel('pa_optional', 'pa_expiring') ? (num(f.pa_premium) || 0) : 0)
      : (f.pi_on && dflSel('pi_optional', 'pi_expiring') ? (num(f.pi_premium) || 0) : 0)
        + (f.adv_on && dflSel('adv_optional', 'adv_expiring') ? (num(f.adv_premium) || 0) : 0));
  const onTopTotal = (f.tria_on && dflSel('tria_optional', 'tria_expiring') ? (num(f.tria_premium) || 0) : 0)
    + (f.war_on && dflSel('war_optional', 'war_expiring') ? (num(f.war_premium) || 0) : 0);
  const minPremium = num(f.minimum_premium) || 0;
  const minApplies = minPremium > 0 && floorableTotal < minPremium;
  const baseTotal = (minApplies ? minPremium : floorableTotal) + onTopTotal;

  const buildLines = () => buildLinesFrom(f);

  async function handleSave() {
    if (!dealId || !canSave) return;
    setSaving(true);
    try {
      const res = await logGlQuote({
        deal_id: dealId,
        ...(editing ? { quote_id: quote.id } : {}),
        carrier: f.carrier.trim(),
        effective_date: f.effective_date,
        // As picked (decision 14); blank stays blank and the gate lists it.
        term_months: termMonthsOf(f.term_months),
        client_visible: f.make_visible,
        present: f.make_visible,
        subject_to: f.subject_to.trim() || null,
        // Always sent: null clears a previously entered minimum on edit.
        minimum_premium: num(f.minimum_premium),
        lines: buildLines(),
      });
      if (!res?.success) { toast.error(res?.error || 'Save failed'); return; }
      // Optional source-PDF attach (gap A) — non-fatal: the quote is already saved.
      let attached = false;
      if (f.source_pdf && res.quote_id) {
        try { await attachQuoteSourcePdf(dealId, res.quote_id, f.source_pdf); attached = true; }
        catch (e) { toast.error(`Quote saved, but source PDF attach failed: ${e.message}`); }
      }
      // What the client sees is what the SAVE ANSWER says (S5).
      const shown = res.client_visible === true;
      const { refused, stillShown } = presentSaveNotices(res);
      toast.success((editing
        ? 'GL quote updated'
        : (f.make_visible && shown ? 'GL quote logged + presented to client' : 'GL quote logged')) + (attached ? ' (source PDF attached)' : ''));
      if (refused) toast.error(refused, { duration: Infinity });
      if (stillShown) toast.info(stillShown);
      onSaved?.(res);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.body?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  // Lowercase render-helpers, CALLED as functions (not <Components/>), so the
  // inputs reconcile by position — no remount/focus-loss and no nested-component
  // lint violation (web-v2-hooks-lint).
  const field = (label, k, ph = '$') => (
    <label className="block" key={k}>
      <span className="mb-1 block text-[11px] text-text-dim">{label}</span>
      <Input value={f[k]} onChange={(e) => set(k, e.target.value)} placeholder={ph} />
    </label>
  );
  // Currency field — formats $#,### live so limits/premiums are unambiguous.
  const moneyField = (label, k, ph = '$0') => (
    <label className="block" key={k}>
      <span className="mb-1 block text-[11px] text-text-dim">{label}</span>
      <Input inputMode="decimal" value={formatMoney(f[k])} onChange={(e) => set(k, formatMoney(e.target.value))} placeholder={ph} />
    </label>
  );
  const toggle = (label, onKey, body) => (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm text-text">
        <input type="checkbox" checked={f[onKey]} onChange={(e) => set(onKey, e.target.checked)} />
        {label}
      </label>
      {f[onKey] ? <div className="space-y-2 rounded-card border border-border p-3">{body}</div> : null}
    </div>
  );
  // §4d — optional-election controls for an electable coverage. "Optional" lets
  // the client check/uncheck it in Tower; "On expiring policy" is its default
  // checkbox state there (renewals: match what the client carried).
  const optRow = (optKey, expKey) => (
    <div className="flex flex-wrap gap-x-5 gap-y-1 border-t border-border pt-2">
      <label className="flex items-center gap-2 text-xs text-text-muted">
        <input type="checkbox" checked={f[optKey]} onChange={(e) => set(optKey, e.target.checked)} />
        Optional (client can decline)
      </label>
      {f[optKey] ? (
        <label className="flex items-center gap-2 text-xs text-text-muted">
          <input type="checkbox" checked={f[expKey]} onChange={(e) => set(expKey, e.target.checked)} />
          On expiring policy (default checked)
        </label>
      ) : null}
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{editing ? 'Edit GL quote' : 'Log GL quote'}</SheetTitle>
          <SheetDescription>
            {editing
              ? 'Update the carrier quote in place. The Tower card and application follow on save. A coverage the carrier includes at no charge is entered at $0.'
              : "Enter a carrier's Airport GL quote. It presents in the client's Tower and is bindable. Premiums are pre-tax. Premises is the base; toggle on the coverages the carrier quoted. A coverage included at no charge is entered at $0."}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-5 py-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-[11px] text-text-dim">Carrier *</span>
              {carrierOther ? (
                <div className="flex gap-1">
                  <Input value={f.carrier} onChange={(e) => set('carrier', e.target.value)} placeholder="Carrier name" autoFocus />
                  <button type="button" onClick={() => { setCarrierOther(false); set('carrier', ''); }} className="shrink-0 rounded-card border border-border px-2 text-xs text-text-muted hover:text-text" title="Back to list">↩</button>
                </div>
              ) : (
                <select
                  value={f.carrier}
                  onChange={(e) => { if (e.target.value === '__other__') { setCarrierOther(true); set('carrier', ''); } else set('carrier', e.target.value); }}
                  className="flex min-h-[44px] w-full rounded-card border border-border bg-bg px-3 py-2 text-sm text-text focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1 focus:ring-offset-bg"
                >
                  <option value="">Select carrier…</option>
                  {carriers.map((c) => <option key={c} value={c}>{c}</option>)}
                  <option value="__other__">Other (type a name)…</option>
                </select>
              )}
            </label>
            <label className="block"><span className="mb-1 block text-[11px] text-text-dim">Effective date *</span><Input type="date" value={f.effective_date} onChange={(e) => set('effective_date', e.target.value)} /></label>
            {/* THE TERM, which this sheet never sent: the server used to fill
                12 (decision 14). 12 pre-picked on a new log; an edit shows the
                stored term, and a stored blank shows nothing. */}
            <div className="col-span-2">
              <TermPicker value={f.term_months} onChange={(v) => set('term_months', v)} disabled={saving} label="Term" />
            </div>
          </div>

          {/* Premises Operations — base */}
          <div className="space-y-2 rounded-card border border-border p-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-text-dim">Premises Liability (base)</h3>
            <div className="grid grid-cols-3 gap-2">
              {moneyField('Each occurrence', 'premises_each_occurrence')}
              {moneyField('Each person', 'premises_each_person')}
              {moneyField('Premium *', 'premises_premium')}
            </div>
          </div>

          {toggle('Medical Expenses', 'medical_on', (
            <>
              <div className="grid grid-cols-4 gap-2">
                {moneyField('Each occ', 'medical_each_occurrence')}
                {moneyField('Each person', 'medical_each_person')}
                {moneyField('Aggregate', 'medical_aggregate')}
                {moneyField('Premium *', 'medical_premium')}
              </div>
              {optRow('medical_optional', 'medical_expiring')}
            </>
          ))}

          {toggle('Products & Completed Operations', 'products_on', (
            <>
              <div className="grid grid-cols-4 gap-2">
                {moneyField('Each occ', 'products_each_occurrence')}
                {moneyField('Aggregate', 'products_aggregate')}
                {moneyField('Sub-limit', 'products_sub_limit')}
                {moneyField('Premium *', 'products_premium')}
              </div>
              {optRow('products_optional', 'products_expiring')}
            </>
          ))}

          {toggle('Fire Legal Liability', 'fire_on', (
            <>
              <div className="grid grid-cols-2 gap-2">
                {moneyField('Each occurrence', 'fire_each_occurrence')}
                {moneyField('Premium *', 'fire_premium')}
              </div>
              {optRow('fire_optional', 'fire_expiring')}
            </>
          ))}

          {toggle("Hangarkeeper's Legal Liability", 'hk_on', (
            <>
              <div className="grid grid-cols-2 gap-2">
                {moneyField('Each occurrence', 'hk_each_occurrence')}
                {moneyField('Each aircraft', 'hk_each_aircraft')}
                {moneyField('Deductible', 'hk_deductible')}
                {moneyField('Premium *', 'hk_premium')}
              </div>
              {optRow('hk_optional', 'hk_expiring')}
            </>
          ))}

          {/* Personal & Advertising Injury: the carrier's document decides the shape. */}
          <div className="space-y-2 rounded-card border border-border p-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-text-dim">Personal &amp; Advertising Injury</h3>
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-text-muted">
              <span>Carrier quotes as:</span>
              <label className="flex items-center gap-1.5">
                <input type="radio" name="pa_mode" checked={f.pa_mode === 'combined'} onChange={() => set('pa_mode', 'combined')} />
                Combined (one coverage, one premium)
              </label>
              <label className="flex items-center gap-1.5">
                <input type="radio" name="pa_mode" checked={f.pa_mode === 'separate'} onChange={() => set('pa_mode', 'separate')} />
                Separate (Personal Injury + Advertising Injury)
              </label>
            </div>
            {f.pa_mode === 'combined' ? toggle('Personal & Advertising Injury', 'pa_on', (
              <>
                <div className="grid grid-cols-3 gap-2">
                  {moneyField('Each occurrence', 'pa_each_occurrence')}
                  {moneyField('Aggregate', 'pa_aggregate')}
                  {moneyField('Premium *', 'pa_premium')}
                </div>
                {optRow('pa_optional', 'pa_expiring')}
              </>
            )) : (
              <>
                {toggle('Personal Injury', 'pi_on', (
                  <>
                    <div className="grid grid-cols-3 gap-2">
                      {moneyField('Each occurrence', 'pi_each_occurrence')}
                      {moneyField('Aggregate', 'pi_aggregate')}
                      {moneyField('Premium *', 'pi_premium')}
                    </div>
                    {optRow('pi_optional', 'pi_expiring')}
                  </>
                ))}
                {toggle('Advertising Injury', 'adv_on', (
                  <>
                    <div className="grid grid-cols-3 gap-2">
                      {moneyField('Each occurrence', 'adv_each_occurrence')}
                      {moneyField('Aggregate', 'adv_aggregate')}
                      {moneyField('Premium *', 'adv_premium')}
                    </div>
                    {optRow('adv_optional', 'adv_expiring')}
                  </>
                ))}
              </>
            )}
          </div>

          {toggle('TRIA / Certified Acts of Terrorism', 'tria_on', (
            <>
              <div className="max-w-[160px]">{moneyField('Premium *', 'tria_premium')}</div>
              {optRow('tria_optional', 'tria_expiring')}
            </>
          ))}

          {toggle('War Risk Liability', 'war_on', (
            <>
              <div className="max-w-[160px]">{moneyField('Premium *', 'war_premium')}</div>
              {optRow('war_optional', 'war_expiring')}
            </>
          ))}

          {/* Carrier minimum policy premium: floors everything but TRIA and War. */}
          <div className="max-w-[220px]">{moneyField('Minimum policy premium (carrier)', 'minimum_premium', 'none stated')}</div>

          {/* Subjectivities (broker-internal) */}
          <label className="block">
            <span className="mb-1 block text-[11px] text-text-dim">Subjectivities (broker-internal — not shown to client)</span>
            <Input value={f.subject_to} onChange={(e) => set('subject_to', e.target.value)} placeholder="e.g. signed application; loss runs" />
          </label>

          {/* Source quote PDF (optional, broker-internal) — drag/drop or click.
              Also the carrier doc the TRIA election overlays onto at bind. */}
          <div className="block">
            <span className="mb-1 block text-[11px] text-text-dim">Source quote PDF (optional — the carrier&apos;s quote document; the TRIA election prints on this)</span>
            <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => takeFile(e.target.files?.[0])} />
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); takeFile(e.dataTransfer.files?.[0]); }}
              className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-card border-2 border-dashed px-4 py-6 text-center transition ${dragOver ? 'border-accent bg-accent-soft' : 'border-border hover:border-accent/50'}`}
            >
              {f.source_pdf ? (
                <div className="flex items-center gap-2 text-sm text-text">
                  <FileText className="h-4 w-4 text-accent" />
                  <span className="max-w-[260px] truncate">{f.source_pdf.name}</span>
                  <button type="button" onClick={(e) => { e.stopPropagation(); set('source_pdf', null); if (fileRef.current) fileRef.current.value = ''; }} className="text-text-muted hover:text-text" title="Remove"><X className="h-3.5 w-3.5" /></button>
                </div>
              ) : (
                <>
                  <UploadCloud className={`h-6 w-6 ${dragOver ? 'text-accent' : 'text-text-dim'}`} />
                  <span className="text-xs text-text-muted">{dragOver ? 'Drop the PDF here' : 'Drag & drop the carrier quote PDF, or click to browse'}</span>
                </>
              )}
            </div>
          </div>

          {/* Total preview + visibility */}
          <div className="rounded-card border border-border p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-muted">Total premium (presented)</span>
              <span className="font-semibold text-text">${baseTotal.toLocaleString('en-US')}</span>
            </div>
            {minApplies ? (
              <p className="mt-1 text-[11px] text-text-dim">Carrier minimum premium of ${minPremium.toLocaleString('en-US')} applies (selected coverages total ${floorableTotal.toLocaleString('en-US')}; TRIA and War on top).</p>
            ) : null}
            <label className="mt-2 flex items-center gap-2 text-sm text-text">
              <input type="checkbox" checked={f.make_visible} onChange={(e) => set('make_visible', e.target.checked)} />
              Make visible to client now (moves the deal to Presented)
            </label>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {saving ? (<><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />Saving…</>) : (editing ? 'Save changes' : 'Log quote')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
